import { z } from "zod";
import { ALBUM_GROUPS, UPLOAD_CHANNELS } from "@/lib/enums";

/** The largest photo a PM may upload, field app or web. */
export const MAX_UPLOAD_BYTES = 60 * 1024 * 1024;

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;

/** Whether "YYYY-MM-DDTHH:mm:ss" names a real calendar time (no Feb 30, no 25:00). */
function isRealWallClock(value: string): boolean {
  const m = WALL_CLOCK.exec(value);
  if (!m) return false;
  const [y, mo, d, h, mi, s] = m.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const date = new Date(Date.UTC(y, mo - 1, d, h, mi, s));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === mo - 1 &&
    date.getUTCDate() === d &&
    date.getUTCHours() === h &&
    date.getUTCMinutes() === mi &&
    date.getUTCSeconds() === s
  );
}

const DECIMAL = /^-?\d+(\.\d+)?$/;

const decimal = (name: string, bound: number) =>
  z
    .string()
    .regex(DECIMAL, `${name} must be a decimal number`)
    .transform(Number)
    .refine((n) => Math.abs(n) <= bound, `${name} must be between -${bound} and ${bound}`);

/**
 * The text fields of `POST /api/projects/:id/uploads` (multipart). Empty fields are dropped
 * before parsing, so "" means "not sent". The file itself is checked by the handler.
 */
export const uploadFieldsSchema = z
  .object({
    id: z.uuid({ error: "The upload id must be a UUID" }),
    albumId: z.string({ error: "Choose an album" }).min(1, "Choose an album"),
    itemId: z.string().min(1).optional(),
    via: z.enum(UPLOAD_CHANNELS, { error: "via must be field or web" }),
    capturedAt: z
      .string()
      .refine(isRealWallClock, "capturedAt must be a time like 2026-11-03T07:45:12")
      .optional(),
    lat: decimal("lat", 90).optional(),
    lng: decimal("lng", 180).optional(),
    accuracy: z
      .string()
      .regex(/^\d+(\.\d+)?$/, "accuracy must be a number of metres")
      .transform(Number)
      .optional(),
    // Blank after trimming means no device was given.
    device: z
      .string()
      .trim()
      .max(120, "device must be 120 characters or fewer")
      .transform((v) => v || undefined)
      .optional(),
    keepDuplicate: z.literal("1", { error: 'keepDuplicate must be "1"' }).optional(),
  })
  .refine((f) => (f.lat === undefined) === (f.lng === undefined), {
    error: "lat and lng must be sent together",
  });

const uploadedPhotoSchema = z.object({
  photoId: z.string(),
  albumId: z.string(),
  itemId: z.string().nullable(),
  fileName: z.string(),
});

/** 201 (a new photo) or 200 with `alreadyUploaded` (this id was seen before). */
export const uploadResponseSchema = z.object({
  data: uploadedPhotoSchema,
  alreadyUploaded: z.literal(true).optional(),
});

/** 409: the bytes (or name and size) match a photo already in the project. */
export const uploadDuplicateSchema = z.object({
  error: z.literal("duplicate"),
  duplicate: z.object({ photoId: z.string(), fileName: z.string() }),
});

const captureAlbumSchema = z.object({
  id: z.string(),
  name: z.string(),
  groupName: z.enum(ALBUM_GROUPS),
  isMc: z.boolean(),
  sortOrder: z.number(),
  photoCount: z.number(),
  itemCount: z.number(),
});

const captureProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  spNumber: z.string().nullable(),
  /** The average of the project's photos' GPS; null when none has any. */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  albums: z.array(captureAlbumSchema),
});
export type CaptureProject = z.infer<typeof captureProjectSchema>;

/** `GET /api/capture/projects`: what the field app needs to pick where a photo goes. */
export const captureProjectsResponseSchema = z.object({ data: z.array(captureProjectSchema) });
